import {
  PencilIcon,
  Trash2Icon,
  UserIcon,
  XSquareIcon,
  ArrowLeftIcon
} from "lucide-react";
import { CopyIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { deleteWorkflow } from "@/api/workflow";
import { createWorkflow } from "@/api/workflow";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { useWorkflows } from "@/hooks/server-state/use-workflows";
import { useWorkflowModalStore } from "@/hooks/store/use-workflow-modal-store";

import { formatCategoryNames } from "@/utils/category";
import { extractCategories } from "@/utils/category";

import AlertModal from "@/components/common/modals/alert-modal";
import RoleListModal from "@/components/common/modals/role-list-modal";

import TableSearch, {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow
} from "@/components/ui/table";

import type { CategoryType } from "@/types/category";
import type { WorkflowType } from "@/types/workflow/workflow";

type CategorySummary = CategoryType & {
  workflowCount: number;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const getPageSize = (value: string | null) => {
  const requestedSize = Number(value);

  return PAGE_SIZE_OPTIONS.includes(requestedSize)
    ? requestedSize
    : DEFAULT_PAGE_SIZE;
};

const getCurrentPage = (value: string | null) => {
  const requestedPage = Number(value);

  return Number.isFinite(requestedPage) && requestedPage > 0
    ? requestedPage
    : 1;
};

const matchesWorkflowSearch = (workflow: WorkflowType, searchValue: string) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  if (!normalizedSearchValue) {
    return true;
  }

  return [workflow.name, workflow.description]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedSearchValue));
};

const WorkflowsPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: res, isLoading } = useWorkflows({ page: 1, size: 50, search: "" });
  const { mutate, isPending } = useMutation({
    mutationFn: (workflowId: number) => deleteWorkflow(workflowId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workflows"] })
  });
  const { onOpen, setWorkflow } = useWorkflowModalStore();

  const navigate = useNavigate();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedForDelete, setSelectedForDelete] = useState<number | null>(
    null
  );

  const { mutate: cloneWorkflow } = useMutation({
    mutationFn: createWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
    }
  });

  const handleClone = (workflow: any) => {
    cloneWorkflow({
      id: 0,
      name: `${workflow.name} (copy)`,
      description: workflow.description ?? ""
    });
  };

  const [isRoleListModalOpen, setIsRoleListModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);

  const allWorkflows = res?.data ?? [];
  const searchValue = searchParams.get("search") ?? "";
  const currentPage = getCurrentPage(searchParams.get("page"));
  const pageSize = getPageSize(searchParams.get("size"));
  const isAllWorkflowsView = searchParams.get("view") === "all";

  const selectedCategoryIdValue = Number(searchParams.get("categoryId"));
  const selectedCategoryId =
    !isAllWorkflowsView && Number.isFinite(selectedCategoryIdValue) && selectedCategoryIdValue > 0
      ? selectedCategoryIdValue
      : null;

  const categoryRows = useMemo<CategorySummary[]>(() => {
    const categoriesMap = new Map<number, CategorySummary>();

    allWorkflows.forEach((workflowItem: WorkflowType) => {
      extractCategories(workflowItem).forEach((category) => {
        const existingCategory = categoriesMap.get(category.id);

        if (existingCategory) {
          existingCategory.workflowCount += 1;
          return;
        }

        categoriesMap.set(category.id, {
          ...category,
          workflowCount: 1
        });
      });
    });

    return Array.from(categoriesMap.values()).sort((firstCategory, secondCategory) =>
      firstCategory.name.localeCompare(secondCategory.name, "fa")
    );
  }, [allWorkflows]);

  const selectedCategory = useMemo(
    () =>
      selectedCategoryId
        ? categoryRows.find((category) => category.id === selectedCategoryId) ?? null
        : null,
    [categoryRows, selectedCategoryId]
  );

  const filteredWorkflows = useMemo(() => {
    if (isAllWorkflowsView) {
      return allWorkflows.filter((workflowItem: WorkflowType) => matchesWorkflowSearch(workflowItem, searchValue));
    }

    if (!selectedCategory) {
      return [];
    }

    return allWorkflows.filter((workflowItem: WorkflowType) => {
      const categoryIds = extractCategories(workflowItem).map((category) => category.id);

      return categoryIds.includes(selectedCategory.id) && matchesWorkflowSearch(workflowItem, searchValue);
    });
  }, [allWorkflows, isAllWorkflowsView, searchValue, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredWorkflows.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const visibleWorkflows = filteredWorkflows.slice(startIndex, startIndex + pageSize);

  const handleCategorySelect = (categoryId: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("categoryId", categoryId.toString());
      params.delete("view");
      params.set("page", "1");
      params.delete("size");
      return params;
    });
  };

  const handleAllWorkflowsSelect = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("view", "all");
      params.delete("categoryId");
      params.set("page", "1");
      params.delete("size");
      return params;
    });
  };

  const handleBackToCategories = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("view");
      params.delete("categoryId");
      params.delete("page");
      params.delete("size");
      return params;
    });
  };

  useEffect(() => {
    if (!selectedCategory && !isAllWorkflowsView) {
      return;
    }

    if (isAllWorkflowsView) {
      return;
    }

    // Avoid resetting page from URL before data is ready.
    if (isLoading) {
      return;
    }

    if (currentPage > totalPages) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", totalPages.toString());
        return params;
      });
    }
  }, [currentPage, isAllWorkflowsView, isLoading, selectedCategory, setSearchParams, totalPages]);

  if (isLoading) return <Loading />;

  return (
    <>
      <AlertModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isPending}
        onConfirm={async () => {
          mutate(selectedForDelete!);
          setIsDeleteModalOpen(false);
        }}
        title="آیا از حذف گردش کار اطمینان دارید؟"
        description="این عملیات قابل برگشت نخواهد بود و گردش کار بصورت دائمی حذف خواهد شد"
      />

      <RoleListModal
        isOpen={isRoleListModalOpen}
        onClose={() => setIsRoleListModalOpen(false)}
        workflowId={selectedRole!}
      />
      {selectedCategory || isAllWorkflowsView ? (
        <>
          <div className="flex items-start justify-between gap-3 px-5 py-2">
            <div className="flex flex-col gap-2">
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={handleBackToCategories}
              >
                <ArrowLeftIcon size={14} className="text-primary" />
                بازگشت به دسته بندی ها
              </button>

              <div className="text-sm text-slate-600">
                {isAllWorkflowsView ? (
                  <>همه گردش کار ها</>
                ) : (
                  <>
                    گردش کارهای دسته بندی <span className="font-semibold text-slate-900">{selectedCategory?.name}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <TableSearch className="h-8 w-full sm:w-72" />
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={() => {
                  onOpen();
                  setWorkflow(null);
                }}
              >
                <XSquareIcon size={14} className="text-primary" />
                ساخت گردش کار جدید
              </button>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام</TableHead>
                <TableHead>توضیحات</TableHead>
                <TableHead>دسته بندی ها</TableHead>
                <TableHead>نقش ها</TableHead>
                <TableHead>ویرایش</TableHead>
                <TableHead>کلون</TableHead>
                <TableHead>حذف</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleWorkflows.map((workflow, index) => (
                <TableRow
                  key={workflow.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(workflow.id.toString());
                  }}
                  className="cursor-pointer"
                >
                  <TableCell>{startIndex + index + 1}</TableCell>
                  <TableCell>{workflow?.name}</TableCell>
                  <TableCell>{workflow?.description}</TableCell>
                  <TableCell className="max-w-xs whitespace-normal">
                    {formatCategoryNames(workflow) || "-"}
                  </TableCell>

                  <TableCell>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsRoleListModalOpen(true);
                        setSelectedRole(workflow.id);
                      }}
                    >
                      <UserIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setWorkflow(workflow);
                        onOpen();
                      }}
                    >
                      <PencilIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClone(workflow);
                      }}
                      title="Clone"
                    >
                      <CopyIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedForDelete(workflow.id);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      <Trash2Icon className="text-red-500" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!visibleWorkflows.length && (
            <EmptyState message="گردش کاری برای این دسته بندی پیدا نشد" />
          )}

          <TablePagination totalItems={filteredWorkflows.length} />
        </>
      ) : (
        <>
          <div className="flex items-center justify-between px-5 py-2">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-slate-900">دسته بندی گردش کار ها</p>
              <p className="mt-1 text-xs text-slate-500">یک دسته بندی را انتخاب کنید تا گردش کارهای مربوط به آن نمایش داده شود.</p>
            </div>

            <button
              className="flex items-center gap-x-1 text-sm hover:text-primary"
              onClick={() => {
                onOpen();
                setWorkflow(null);
              }}
            >
              <XSquareIcon size={14} className="text-primary" />
              ساخت گردش کار جدید
            </button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام دسته بندی</TableHead>
                <TableHead>تعداد گردش کار</TableHead>
                <TableHead>توضیحات</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              <TableRow className="cursor-pointer" onClick={handleAllWorkflowsSelect}>
                <TableCell>1</TableCell>
                <TableCell className="font-medium text-primary">همه</TableCell>
                <TableCell>{allWorkflows.length}</TableCell>
                <TableCell>نمایش همه گردش کار ها</TableCell>
              </TableRow>

              {categoryRows.map((categoryItem, index) => (
                <TableRow
                  key={categoryItem.id}
                  className="cursor-pointer"
                  onClick={() => handleCategorySelect(categoryItem.id)}
                >
                  <TableCell>{index + 2}</TableCell>
                  <TableCell>{categoryItem.name}</TableCell>
                  <TableCell>{categoryItem.workflowCount}</TableCell>
                  <TableCell>{categoryItem.description || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!categoryRows.length && <EmptyState message="دسته بندی پیدا نشد" />}
        </>
      )}
    </>
  );
};

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] w-full items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = ({ message = "گردش کاری پیدا نشد" }: { message?: string }) => (
  <div className="flex h-32 w-full items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">{message}</p>
  </div>
);

export default WorkflowsPage;
